import { TeamSide } from "../constants/teams";
import {
  GameFullResponse,
  GamePlayerRef,
  LineUpPlayer,
} from "../types/gameFull";
import { GameRosters } from "../types/roster";

/**
 * Hitter/pitcher Change and Sub Out (at-bat editing). These apply the change
 * locally (optimistically); api/useGameWrites persists it via omnibus.
 */

export const toPlayerRef = (player: LineUpPlayer): GamePlayerRef => ({
  playerId: player.playerId,
  playerName: player.name,
});

const otherSide = (side: TeamSide): TeamSide =>
  side === "away" ? "home" : "away";

const hasPlayer = (players: LineUpPlayer[], playerId: string) =>
  players.some((p) => p.playerId === playerId);

/**
 * The player's entry in `side`'s lineup, else the other team's or the other
 * players'. A player can be on both teams, with a different slot and jersey
 * number on each, so callers pass the team the half-inning says they're on.
 */
export const findLineupPlayer = (
  data: GameFullResponse,
  playerId: string | undefined,
  side: TeamSide,
) =>
  playerId
    ? [
        ...data.lineUp[side],
        ...data.lineUp[otherSide(side)],
        ...data.lineUp.extra,
      ].find((p) => p.playerId === playerId)
    : undefined;

/**
 * The player's team: `expected` (from the half-inning) unless only the other
 * team's lineup has them. A player on both teams stays on `expected`.
 */
export const getLineupSide = (
  data: GameFullResponse,
  playerId: string,
  expected: TeamSide,
): TeamSide =>
  !hasPlayer(data.lineUp[expected], playerId) &&
  hasPlayer(data.lineUp[otherSide(expected)], playerId)
    ? otherSide(expected)
    : expected;

/** Adds players to a team's roster, skipping anyone already on it. */
export const addRosterPlayers = (
  rosters: GameRosters,
  side: TeamSide,
  players: LineUpPlayer[],
): GameRosters => {
  const roster = rosters[side];
  const newPlayers = players.filter(
    (player) => !roster.players.some((p) => p.playerId === player.playerId),
  );
  return {
    ...rosters,
    [side]: { ...roster, players: [...roster.players, ...newPlayers] },
  };
};

/**
 * Subs `inPlayer` in for `outPlayerId`: they take over the lineup entry
 * (batting order slot + position). If the outgoing player isn't in the lineup
 * (e.g. a pitcher missing from it, or the unknown hitter of a game with no
 * lineup), the sub joins with `fallback`.
 * Live game refs to the outgoing player move to the sub; past PAs keep who
 * actually batted/pitched.
 */
export const substitutePlayer = (
  data: GameFullResponse,
  rosters: GameRosters,
  side: TeamSide,
  outPlayerId: string,
  inPlayer: LineUpPlayer,
  fallback?: Pick<LineUpPlayer, "battingOrder" | "position">,
): { data: GameFullResponse; rosters: GameRosters } => {
  const { battingOrder: _slot, position: _position, ...sub } = inPlayer;
  // A sub already in the lineup (e.g. a non-hitting pitcher) keeps their
  // own slot/position wherever the outgoing player had none
  const existing = data.lineUp[side].find(
    (p) => p.playerId === inPlayer.playerId,
  );
  const lineUp = data.lineUp[side].filter(
    (p) => p.playerId !== inPlayer.playerId,
  );
  const outIndex = lineUp.findIndex((p) => p.playerId === outPlayerId);

  const nextLineUp =
    outIndex === -1
      ? [
          ...lineUp,
          {
            ...sub,
            battingOrder: existing?.battingOrder ?? fallback?.battingOrder,
            position: existing?.position ?? fallback?.position,
          },
        ]
      : lineUp.map((p, index) =>
          index === outIndex
            ? {
                ...sub,
                battingOrder: p.battingOrder ?? existing?.battingOrder,
                position: p.position ?? existing?.position,
              }
            : p,
        );

  const subRef = toPlayerRef(inPlayer);
  const swapRef = (ref: GamePlayerRef | null) =>
    ref?.playerId === outPlayerId ? subRef : ref;

  return {
    data: {
      ...data,
      game: {
        ...data.game,
        currentHitter: swapRef(data.game.currentHitter),
        currentPitcher: swapRef(data.game.currentPitcher),
      },
      lineUp: {
        ...data.lineUp,
        [side]: nextLineUp,
        extra: data.lineUp.extra.filter(
          (p) => p.playerId !== inPlayer.playerId,
        ),
      },
    },
    rosters: addRosterPlayers(rosters, side, [inPlayer]),
  };
};

/**
 * Active at-bat Change Hitter: makes `player` the live hitter and the hitter
 * of the active PA. Frontend only until the next pitch is tagged.
 */
export const setActiveHitter = (
  data: GameFullResponse,
  player: LineUpPlayer,
): GameFullResponse => {
  const ref = toPlayerRef(player);
  return {
    ...data,
    game: { ...data.game, currentHitter: ref },
    plateAppearances: data.plateAppearances.map((pa) =>
      pa.plateAppearanceId === data.game.currentPlateAppearanceId
        ? { ...pa, hitter: ref }
        : pa,
    ),
  };
};
